import { PropertyService } from './../../properties/property.service';
import {
  Component,
  AfterContentInit,
  OnDestroy,
  Input,
  OnInit
} from '@angular/core';
import { PlycaDataService } from '../../../services/plyca.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuditTaskDTO, DossierDTO, TaskDTO, getSimpleIdentifierFromString } from '../../api/plyca-dtos';
import { UserService } from '../../user.service';
import { UIService } from '../../ui.service';
import { forkJoin, throwError } from 'rxjs';
import { Configuration } from '../../conf/app.constants';
import { TaskService } from '../../tasks/task.service';
import { RuleService } from '../rules/RuleService';
import { PaginationDTO } from '../../api/PaginationModel';
import { DossierService } from '../dossier.service';
import {
  HelpService,
  HelpType
} from '../../components/common/help/help.service';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import { tap, finalize, catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ServiceLocator } from '../../ServiceLocator';
import { BaseComponent } from '../../components/common/baseComponent/base.component';
import { InboxUsersComponent } from '../audit/inboxusers.component';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { AppConfiguration } from '../../conf/app.configuration';
import { multiSignatureComponent } from '../../tasks/multiSignature/multiSignature.component';
import { AppState } from '../../store/AppState';
import { Store } from '@ngrx/store';
import { MANUALPROCESSINGMODE_ID } from '../procedures/procedureCircuit.component';

@Component({
  selector: 'dossierActions',
  templateUrl: 'dossierActions.template.html',
  providers: [DialogService]
})
export class DossierActionsComponent extends BaseComponent implements OnDestroy, AfterContentInit, OnInit {
  loaded = false;

  @Input()
  showAudit = true;
  userTasks: TaskDTO[];

  dossierId: string;
  dossier: any;
  auditList: Array<any>;
  helpLabels: Array<string> = [];
  showRunnigRules = false;
  propertyService: PropertyService;
  visibleRunningRules = false;
  multiSignatureAvailable = true;

  constructor(
    public plycaService: PlycaDataService,
    public taskService: TaskService,
    public ruleService: RuleService,
    public userService: UserService,
    public dossierService: DossierService,
    public uiService: UIService,
    public helpService: HelpService,
    public route: ActivatedRoute,
    public router: Router,
    public dialogService: DialogService,
    public configuration: Configuration,
    private headerActionsService: HeaderActionsService,
    private waitService: WaitLoadingService,
    private plycaErrorHandler: PlycaErrorHandler,
    protected appConfig: AppConfiguration,
    public translate: TranslateService,
    private store: Store<AppState>,
  ) {
    super();
    this.subscribe(route.params, params => {
      // eliminar parametros de querystring si vienen
      this.dossierId = params['id'].split('?')[0];
    });
    // this.helpService.showContextHelp([HelpType.DOSSIER_ACTIONS]);
    this.addHelpLabel(HelpType.DOSSIER_ACTIONS);
    this.propertyService = ServiceLocator.injector.get(PropertyService);
    this.subscribe(this.store.select('pendingTasks'),
      (tasks: Array<TaskDTO>) => {
        this.userTasks = tasks;
      }
    );
  }

  ngOnInit() {
    this.load();
  }

  ngAfterContentInit(): void {
  }

  ngOnDestroy() {
    this.headerActionsService.clearActions();
    super.ngOnDestroy();
  }

  private setSubHeaderActions() {
    this.headerActionsService.clearActions();
    if (this.visibleRunningRules && getSimpleIdentifierFromString(this.dossier?.modoTramitacion?.identifier) !== MANUALPROCESSINGMODE_ID) {
      if (this.showRunnigRules) {
        this.subscribe(this.headerActionsService
          .addAction('DossierActions.filters.showRunningRules', 'far fa-check-square', 'btn-outline-primary', true),
          () => {
            this.showRunnigRules = false;
            this.setSubHeaderActions();
            this.load();
          });
      } else {
        this.subscribe(this.headerActionsService
          .addAction('DossierActions.filters.showRunningRules', 'far fa-square', 'btn-outline-primary', true),
          () => {
            if (this.visibleRunningRules) {
              this.showRunnigRules = true;
            } else {
              this.showRunnigRules = false;
            }
            this.setSubHeaderActions();
            this.load();
          });
      }
    }

    let multiSignatureEnabled = this.appConfig.getValue('multiSignatureEnabled');
    if (multiSignatureEnabled) {
      this.headerActionsService.addDivider();
      this.subscribe(this.headerActionsService
        .addAction(
          'MultiSignature.action',
          'fas fa-lg fa-pen-nib',
          'btn-secondary',
          this.multiSignatureAvailable
        ),
        () => {
          this.openMultiSignatureComponent();
        });
    }
    this.headerActionsService.addDivider();
    this.subscribe(this.headerActionsService
      .addAction('General.reload', 'fas fa-lg fa-sync-alt', 'btn-primary', true),
      () => {
        this.load();
      });
    this.headerActionsService.addDivider();
    this.subscribe(this.headerActionsService
      .addAction(
        'General.back',
        'fas fa-chevron-left',
        'btn-outline-secondary',
        true
      ),
      () => {
        this.uiService.routeBack();
      });
  }

  isShowAudit() {
    return this.showAudit;
  }

  load() {
    this.loaded = false;
    this.waitService.startWaiting();
    // Actualizar reglas
    this.subscribe(forkJoin([
      this.plycaService.getDossier(this.dossierId).pipe(
        tap((_dossier: DossierDTO) => {
          this.dossier = _dossier;
        })
      ),
      this.plycaService.getAuditTasksExp(this.dossierId, true).pipe(
        tap((param: PaginationDTO<AuditTaskDTO>) => {
          if (param && param.content) {
            this.auditList = param.content.filter(x => {
              // Si es una regla y no está despachada tiene peculiaridades
              if (x.rule && !x.dateDesp) {
                // Si no especifica perfil lo tenemos ya como una accion disponible
                if (!x.profiles) {
                  return false;
                }
                // Solo tendremos que mostrar las acciones de otros usuarios, si nosotros tenemos perfil ya sera una accion disponible
                let stPerfiles = '';
                let separador = '';
                x.profiles.forEach((profile) => {
                  stPerfiles += separador + profile.identifier;
                  separador = ',';
                });
                return x.profiles && (!this.userService.hasRoles(stPerfiles));

              }
              // 1. Filtrar las tareas de supervisor, usuErrores y usuaut)
              // 2. Filtrar las tareas que pertenece a algun buzon que el usuario de la sesion tiene asociado
              return (
                !x.dateDesp &&
                (x.inbox &&
                  (x.inbox.identifier !== 'supervisor' &&
                    x.inbox.identifier !== 'usuErrores' &&
                    x.inbox.identifier !== '-' &&
                    !x.inbox.identifier.startsWith('usuaut'))) &&
                !this.userService
                  .getUser()
                  .inboxes.find(
                    userInbox => userInbox.identifier === x.inbox.identifier
                  )
                && (!this.userTasks || (this.userTasks && !this.userTasks.find(task => task.identifier === x.swIdTask)))
              );
            });
          } else {
            return [];
          }
        })
      )
    ])
      .pipe(
        catchError((error: any) => {
          /* this.uiService.showSuccess(
            this.translate.instant('General.serverError'),
            this.translate.instant('General.error'),
            'error'
          ); */
          if (error.errors) {
            // let errores: Array<ValidationExecutionDTO> = error.errors;
            // this.evaluationError = '';
            // errores.forEach((err: ValidationExecutionDTO) => {
            //   this.evaluationError += err.message;
            // });
          }
          return throwError(error);
        }),
        finalize(() => {
          this.hasProfileToShowRunningRules();

          // Una vez obtenidos todos los datos cargamos la ayuda
          this.loadHelp();
          this.loaded = true;
          this.waitService.cancelWaiting();
        })
      ));
  }

  public isLoaded() {
    return this.loaded;
  }

  addHelpLabel(label: string) {
    if (this.helpLabels.indexOf(label) === -1) {
      this.helpLabels.push(label);
    }
  }

  loadHelp() {
    console.log('CARGANDO AYUDA', this.helpLabels.join(' '));
    if (this.helpLabels) {
      //      this.helpService.showContextHelp(this.helpLabels.join(' '));
      this.subscribe(this.helpService.showContextHelpObservable(this.helpLabels));
    }
  }

  showErrorMessage(err) {
    this.plycaErrorHandler.showServerError(err);
  }

  hasProfileToShowRunningRules(): void {
    //check de docuemtno completo
    this.subscribe(this.propertyService.getProperty('PERFILES_DESCARTAR_TAREAS'), (value: string) => {
      if (value == null || value === '') {
        this.visibleRunningRules = false;
      } else {
        value = value.replace(new RegExp(/[;]/g), ',');
        if (this.userService.hasRoles(value)) {
          this.visibleRunningRules = true;
        } else {
          this.visibleRunningRules = false;
        }
      }
      if (getSimpleIdentifierFromString(this.dossier?.modoTramitacion?.identifier) === MANUALPROCESSINGMODE_ID) {
        this.showRunnigRules = true;
      }
      this.setSubHeaderActions();
    });
  }

  showInbox(element: any) {
    let config: DynamicDialogConfig = {
      header: this.translate.instant('Audit.UsersTaskAccess.title') + ': (' + element.inbox.identifier + ') ' + element.inbox.name + '.',
      width: '60%',
      style: { 'max-height': '60%' },
      styleClass: 'helpStyle'
    };
    config['taskId'] = element.swIdTask;
    config['inboxId'] = element.inbox.identifier;
    config['inboxDescription'] = element.inbox.name ? element.inbox.name : element.inbox.identifier;
    const ref = this.dialogService.open(InboxUsersComponent, config);
    let curentHelpTags = [];
    this.subscribe(
      this.helpService.getStoreTagsObservable().pipe(
        switchMap((helpTags) => curentHelpTags = helpTags)));
    this.subscribe(ref.onClose,
      () => {
        this.subscribe(this.helpService.showContextHelpObservable(curentHelpTags));
      });
  }

  openMultiSignatureComponent(): void {
    let config: DynamicDialogConfig = {
      header: this.translate.instant('MultiSignature.document.signature'),
      width: '75%',
      style: { overflow: 'hidden', 'max-height': '90%' },
      styleClass: 'formEdition',
      closeOnEscape: false,
      data: {
        dossierId: this.dossierId
      }
    };
    let curentHelpTags = [];
    this.subscribe(
      this.helpService.getStoreTagsObservable().pipe(
        switchMap((helpTags) => curentHelpTags = helpTags)));

    const dialog = this.dialogService.open(multiSignatureComponent, config);

    this.subscribe(dialog.onClose,
      () => {
        this.subscribe(this.helpService.showContextHelpObservable(curentHelpTags));
      });
  }
}
